import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import 'react-toastify/dist/ReactToastify.css';
import ViewCompanyModal from '../../components/ViewCompanyModal';
import Select from 'react-select'; // Add this import


const DomainAccessManager = ({ domains, setDomains, defaultAccess }) => {
  // Helper function to format option labels
  const formatLabel = (option) => {
    return option.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Feature descriptions for tooltips
  const getFeatureDescription = (feature) => {
    const descriptions = {
      // Core & Admin
      'dashboard': 'Main dashboard access with overview',
      'delivery-order': 'Create and manage new delivery orders',
      'items': 'Manage inventory items and products',
      'crm': 'Customer Relationship Management system',
      'quotation': 'Create and manage quotations',
      'quotation-new': 'Enhanced quotation management system',
      'tally-quotation': 'Integrate quotations with Tally',
      
      // Employee Management
      'employee-management': 'Manage employee records and profiles',
      'leave-management': 'Handle employee leave requests and approvals',
      'attendance-management': 'Track and manage employee attendance',
      'payroll-management': 'Process employee salaries and payroll',
      'role-management': 'Manage user roles and permissions',
      
      // Customer & Dealer Management
      'dealer-management': 'Manage dealer relationships and data',
      'customer-management': 'Manage customer information and interactions',
      'guest-master': 'Manage guest information and records',
      'buyers': 'Manage buyer information and transactions',
      
      // Inventory & Items
      'item-management': 'Manage inventory items and products',
      'stock-master': 'Manage stock inventory and levels',
      'concept-product': 'Manage concept products and prototypes',
      'purchase-indent': 'Create and manage purchase indents',
      'reference-commission': 'Manage commission calculations and tracking',
      
      // Operations
      'field-visit': 'Track and manage field visits',
      'goods-receipt': 'Process goods receipt and verification',
      'proforma-invoice': 'Create and manage proforma invoices',
      'packing-slip': 'Generate and manage packing slips',
      'estimate': 'Create project estimates and quotations',
      
      // Tiles Management
      'tiles-master': 'Master data for tiles inventory',
      'tiles-order': 'Process and manage tiles orders',
      'tiles-dispatch-entry': 'Create dispatch entries for ordered tiles',
      'tiles-received': 'Receive and verify tiles shipments',
      'tiles-inward': 'Manage inward movement of tiles',
      'tiles-outward': 'Manage outward movement of tiles',
      'tiles-delivery-order': 'Handle tiles delivery orders',
      'tiles-sales-order': 'Create and manage tiles sales orders',
      'tiles-location-transfer': 'Transfer tiles between locations',
      'demolished-tiles': 'Manage demolished or damaged tiles',
      'tiles-return': 'Process tile returns and manage return inventory',
      
      // Tally Integration
      'tally-operations-panel': 'View and monitor Tally sync operations created by company head',
      
      // Stock Management
      'stock-items-inward': 'Process inward stock movements',
      'stock-items-outward': 'Process outward stock movements',
      'stock-items-location-transfer': 'Transfer stock between locations',
      'stock-items-status': 'View current stock status and levels',
      'stock-movement': 'Track all stock movements',
      'stock-items-movement': 'Detailed stock item movement tracking',
      'stock-items-order': 'Create and manage stock items purchase orders',
      'stock-items-dispatch': 'Create dispatch entries for ordered stock items',
      'stock-items-received': 'Receive and verify stock item shipments',
      'stock-items-destroy': 'Destroy/write-off damaged stock items',
      
      // Company Head Features
      'app-dealer-management': 'Manage app-based dealer registrations and data',
      'app-dealer-access-logs': 'View and monitor dealer app access logs',
      'app-dealer-interest-reports': 'View dealer interest and activity reports',
      'tally-management': 'Manage Tally integration settings',
      'whatsapp-automations': 'Configure WhatsApp automations and automated messaging',
      
      // Add more descriptions as needed
    };
    return descriptions[feature] || `Manage ${formatLabel(feature).toLowerCase()}`;
  };

  // Helper function to create grouped options
  const createGroupedOptions = (accessType) => {
    const groupMap = {
      admin: {
        'Core Operations': ['delivery-order', 'items', 'quotation', 'crm'],
        'Tally Integration': ['tally-quotation', 'tally-items'],
        'Management & Reports': ['activity-log', 'campaign-management', 'schemes-reports'],
      },
      companyHead: {
        'Dashboard & Core': ['dashboard'],
        'HR Management': ['employee-management', 'leave-management', 'attendance-management', 'payroll-management', 'role-management'],
        'Asset & Brand Management': ['asset-management', 'brand-management'],
        'System Management': ['tally-management', 'tiles-management'],
        'App Dealer Management': ['app-dealer-management', 'app-dealer-access-logs', 'app-dealer-interest-reports'],
        'Reports & Analytics': ['delivery-orders-reports', 'activity-logs-reports', 'choice-entry-reports', 'quotations-reports', 'proforma-invoice-reports', 'showroom-analytics', 'delivery-orders-detailed-reports'],
        'Financial': ['tiles-day-book', 'tiles-ledger'],
        'Automations': ['whatsapp-automations'],
      },
      employee: {
        'Dashboard & Core': ['dashboard'],
        'Core Operations': ['delivery-order',  'quotation', 'quotation-new', 'estimate', 'proforma-invoice', 'packing-slip', 'goods-receipt'],
        'Customer & Sales': ['tele-calling-sales', 'tele-calling-payment', 'customer-management', 'dealer-management', 'buyers', 'supplier-management', 'guest-master', 'reference-management', 'ma-management'],
        'Inventory & Items': ['item-management', 'purchase-indent', 'reference-commission'],
        'HR & Personal': ['apply-leave', 'attendance', 'field-visit', 'reminders'],
        'Activity & Logs': ['activity-log', 'activity-logs-new', 'choice-entry'],
        'Location & Transport': ['station-management', 'transport-management'],
        'Doors Management': ['doors', 'doors-inward', 'doors-outward', 'doors-stock-status', 'doors-parameters'],
        'Tiles Management': ['tiles-master', 'tiles-parameters', 'tiles-stock-status', 'tiles-inward', 'tiles-outward', 'tiles-delivery-order', 'tiles-sales-order', 'tiles-location-transfer', 'tiles-day-book', 'tiles-ledger','tiles-dispatch-entry', 'tiles-order', 'tiles-received', 'tiles-return'],
        'Tally Integration': ['tally-quotation', 'tally-items-management', 'tally-parameters-management', 'tally-ledger-management', 'tally-operations-panel'],
        'Stock Management': ['stock-master', 'stock-items-inward', 'stock-items-outward', 'stock-items-location-transfer', 'stock-items-status', 'stock-items-ledger', 'stock-items-day-book', 'stock-movement', 'stock-items-movement', 'stock-items-order', 'stock-items-dispatch', 'stock-items-received', 'stock-items-destroy'],
        'Product & Inventory': ['concept-product', 'demolished-tiles']
      }
    };

    const groups = groupMap[accessType] || {};
    return Object.entries(groups).map(([groupName, options]) => ({
      label: groupName,
      options: options.filter(opt => defaultAccess[accessType]?.includes(opt)).map(option => ({
        value: option,
        label: formatLabel(option)
      }))
    })).filter(group => group.options.length > 0);
  };

  // Helper function to create simple options array (fallback)
  const createOptions = (accessArray) => {
    return accessArray.map(option => ({
      value: option,
      label: formatLabel(option)
    }));
  };

  // Helper function to handle select all/clear all
  const handleBulkSelection = (domainIndex, accessType, action) => {
    const newDomains = [...domains];
    if (action === 'selectAll') {
      switch (accessType) {
        case 'admin':
          newDomains[domainIndex].adminDashboardAccess = [...defaultAccess.admin];
          break;
        case 'companyHead':
          newDomains[domainIndex].companyHeadDashboardAccess = [...defaultAccess.companyHead];
          break;
        case 'employee':
          newDomains[domainIndex].employeeDashboardAccess = [...defaultAccess.employee];
          break;
      }
    } else if (action === 'clearAll') {
      switch (accessType) {
        case 'admin':
          newDomains[domainIndex].adminDashboardAccess = [];
          break;
        case 'companyHead':
          newDomains[domainIndex].companyHeadDashboardAccess = [];
          break;
        case 'employee':
          newDomains[domainIndex].employeeDashboardAccess = [];
          break;
      }
    }
    setDomains(newDomains);
  };

  const addDomain = () => {
    setDomains([
      ...domains,
      {
        domainName: '',
        adminDashboardAccess: [],
        companyHeadDashboardAccess: [],
        employeeDashboardAccess: [],
      },
    ]);
  };

  const removeDomain = (index) => {
    if (domains.length <= 1) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'At least one domain is required.',
      });
      return;
    }

    const newDomains = [...domains];
    newDomains.splice(index, 1);
    setDomains(newDomains);
  };

  const updateDomainName = (index, value) => {
    const newDomains = [...domains];
    newDomains[index].domainName = value;
    setDomains(newDomains);
  };



  return (
    <div className="mt-6 col-span-4 border rounded p-4 bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Domain Configuration</h3>
        <button
          type="button"
          onClick={addDomain}
          className="px-3 py-1 bg-blue-600 text-white rounded flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Domain
        </button>
      </div>

      {domains.map((domain, index) => (
        <div key={index} className="mb-6 p-4 bg-white border rounded shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <div className="flex-grow mr-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domain Name *
              </label>
              <input
                type="text"
                value={domain.domainName}
                onChange={(e) => updateDomainName(index, e.target.value)}
                className="w-full p-2 border rounded"
                required
                placeholder="example.com"
              />
            </div>
            {domains.length > 1 && (
              <button
                type="button"
                onClick={() => removeDomain(index)}
                className="px-3 py-1 bg-red-600 text-white rounded h-10 mt-6 flex items-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-700">Dashboard Access for {domain.domainName || 'this domain'}</h4>
              <div className="text-sm bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                <span className="text-blue-800 font-medium">
                  Total Features: {(domain.adminDashboardAccess?.length || 0) + 
                                 (domain.companyHeadDashboardAccess?.length || 0) + 
                                 (domain.employeeDashboardAccess?.length || 0)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="border p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-700 flex items-center">
                    Admin Dashboard Access
                    <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                      {domain.adminDashboardAccess?.length || 0}/{defaultAccess.admin.length}
                    </span>
                  </h5>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'admin', 'selectAll')}
                      className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      title="Select all admin features"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'admin', 'clearAll')}
                      className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Clear all admin features"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <Select
                  isMulti
                  name={`admin-access-${index}`}
                  options={createGroupedOptions('admin')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={domain.adminDashboardAccess?.map(option => ({
                    value: option,
                    label: formatLabel(option)
                  })) || []}
                  onChange={(selectedOptions) => {
                    const newDomains = [...domains];
                    newDomains[index].adminDashboardAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setDomains(newDomains);
                  }}
                  placeholder="Search and select admin dashboard features..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#9ca3af' },
                      '&:focus-within': { borderColor: '#ef4444', boxShadow: '0 0 0 1px #ef4444' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#ef4444',
                      borderRadius: '0.375rem',
                      border: '1px solid #dc2626',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      borderRadius: '0 0.375rem 0.375rem 0',
                      '&:hover': {
                        backgroundColor: '#dc2626',
                        color: 'white',
                      },
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '8px 12px',
                      margin: 0,
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.875rem',
                      paddingLeft: '24px',
                      backgroundColor: state.isSelected ? '#ef4444' : state.isFocused ? '#fef2f2' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#ef4444' : '#fef2f2',
                      }
                    }),
                  }}
                  closeMenuOnSelect={false}
                  isClearable={true}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Selected: {domain.adminDashboardAccess?.length || 0} of {defaultAccess.admin.length}
                </div>
              </div>

              <div className="border p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-700 flex items-center">
                    Company Head Dashboard Access
                    <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                      {domain.companyHeadDashboardAccess?.length || 0}/{defaultAccess.companyHead.length}
                    </span>
                  </h5>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'companyHead', 'selectAll')}
                      className="text-xs px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                      title="Select all company head features"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'companyHead', 'clearAll')}
                      className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Clear all company head features"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <Select
                  isMulti
                  name={`companyHead-access-${index}`}
                  options={createGroupedOptions('companyHead')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={domain.companyHeadDashboardAccess?.map(option => ({
                    value: option,
                    label: formatLabel(option)
                  })) || []}
                  onChange={(selectedOptions) => {
                    const newDomains = [...domains];
                    newDomains[index].companyHeadDashboardAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setDomains(newDomains);
                  }}
                  placeholder="Search and select company head dashboard features..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#9ca3af' },
                      '&:focus-within': { borderColor: '#f59e0b', boxShadow: '0 0 0 1px #f59e0b' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#f59e0b',
                      borderRadius: '0.375rem',
                      border: '1px solid #d97706',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      borderRadius: '0 0.375rem 0.375rem 0',
                      '&:hover': {
                        backgroundColor: '#d97706',
                        color: 'white',
                      },
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '8px 12px',
                      margin: 0,
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.875rem',
                      paddingLeft: '24px',
                      backgroundColor: state.isSelected ? '#f59e0b' : state.isFocused ? '#fef3c7' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#f59e0b' : '#fef3c7',
                      }
                    }),
                  }}
                  closeMenuOnSelect={false}
                  isClearable={true}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Selected: {domain.companyHeadDashboardAccess?.length || 0} of {defaultAccess.companyHead.length}
                </div>
              </div>

              <div className="border p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-700 flex items-center">
                    Employee Dashboard Access
                    <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                      {domain.employeeDashboardAccess?.length || 0}/{defaultAccess.employee.length}
                    </span>
                  </h5>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'employee', 'selectAll')}
                      className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      title="Select all employee features"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSelection(index, 'employee', 'clearAll')}
                      className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Clear all employee features"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <Select
                  isMulti
                  name={`employee-access-${index}`}
                  options={createGroupedOptions('employee')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={domain.employeeDashboardAccess?.map(option => ({
                    value: option,
                    label: formatLabel(option)
                  })) || []}
                  onChange={(selectedOptions) => {
                    const newDomains = [...domains];
                    newDomains[index].employeeDashboardAccess = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                    setDomains(newDomains);
                  }}
                  placeholder="Search and select employee dashboard features..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#9ca3af' },
                      '&:focus-within': { borderColor: '#10b981', boxShadow: '0 0 0 1px #10b981' }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#10b981',
                      borderRadius: '0.375rem',
                      border: '1px solid #059669',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      paddingLeft: '8px',
                      paddingRight: '4px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: 'white',
                      borderRadius: '0 0.375rem 0.375rem 0',
                      '&:hover': {
                        backgroundColor: '#059669',
                        color: 'white',
                      },
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '8px 12px',
                      margin: 0,
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.875rem',
                      paddingLeft: '24px',
                      backgroundColor: state.isSelected ? '#10b981' : state.isFocused ? '#d1fae5' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#10b981' : '#d1fae5',
                      }
                    }),
                  }}
                  closeMenuOnSelect={false}
                  isClearable={true}
                />
                <div className="mt-1 text-xs text-gray-500">
                  Selected: {domain.employeeDashboardAccess?.length || 0} of {defaultAccess.employee.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TermsAndConditionsManager = ({ title, terms, setTerms, placeholder = "Enter term or condition" }) => {
  const addTerm = () => {
    setTerms([...terms, ""]);
  };

  const removeTerm = (index) => {
    const newTerms = terms.filter((_, i) => i !== index);
    setTerms(newTerms);
  };

  const updateTerm = (index, value) => {
    const newTerms = [...terms];
    newTerms[index] = value;
    setTerms(newTerms);
  };

  return (
    <div className="col-span-4 border p-4 bg-gray-50 rounded mt-2">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button
          type="button"
          onClick={addTerm}
          className="px-3 py-1 bg-green-600 text-white rounded flex items-center hover:bg-green-700 transition-colors"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Term
        </button>
      </div>
      
      {terms.length === 0 ? (
        <div className="text-center p-4 bg-blue-50 rounded">
          <p className="text-gray-600">No terms added yet. Click "Add Term" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {terms.map((term, index) => (
            <div key={index} className="flex items-center gap-3 bg-white p-3 rounded border">
              <span className="text-sm font-medium text-gray-500 min-w-[2rem]">{index + 1}.</span>
              <input
                type="text"
                value={term}
                onChange={(e) => updateTerm(index, e.target.value)}
                placeholder={placeholder}
                className="flex-1 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => removeTerm(index)}
                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                title="Remove term"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      
      {terms.length > 0 && (
        <div className="mt-3 text-sm text-gray-600">
          Total terms: {terms.length}
        </div>
      )}
    </div>
  );
};

const StockCalculationFormulaManager = ({ companyId, formulas, setFormulas }) => {
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (companyId) {
      fetchVoucherTypes();
    }
  }, [companyId]);

  const fetchVoucherTypes = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tally/voucher-types/${companyId}`);
      if (response.data.success) {
        setVoucherTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching voucher types:', error);
    } finally {
      setLoading(false);
    }
  };

  const addFormula = () => {
    const newFormula = {
      name: '',
      description: '',
      formula: [{ voucherType: '', operator: '+', order: 0 }],
      isActive: true
    };
    setFormulas([...formulas, newFormula]);
  };

  const removeFormula = (formulaIndex) => {
    setFormulas(formulas.filter((_, idx) => idx !== formulaIndex));
  };

  const updateFormulaField = (formulaIndex, field, value) => {
    const updated = [...formulas];
    updated[formulaIndex][field] = value;
    setFormulas(updated);
  };

  const addFormulaStep = (formulaIndex) => {
    const updated = [...formulas];
    const currentFormula = updated[formulaIndex].formula || [];
    const newOrder = currentFormula.length;
    updated[formulaIndex].formula = [...currentFormula, { voucherType: '', operator: '+', order: newOrder }];
    setFormulas(updated);
  };

  const removeFormulaStep = (formulaIndex, stepIndex) => {
    const updated = [...formulas];
    updated[formulaIndex].formula = updated[formulaIndex].formula
      .filter((_, idx) => idx !== stepIndex)
      .map((step, idx) => ({ ...step, order: idx }));
    setFormulas(updated);
  };

  const updateFormulaStep = (formulaIndex, stepIndex, field, value) => {
    const updated = [...formulas];
    updated[formulaIndex].formula[stepIndex][field] = value;
    setFormulas(updated);
  };

  const getFormulaPreview = (formula) => {
    if (!formula || !formula.formula || formula.formula.length === 0) return 'No formula defined';
    
    return formula.formula
      .sort((a, b) => a.order - b.order)
      .map((step, idx) => {
        const voucherType = voucherTypes.find(vt => vt._id === step.voucherType);
        const voucherName = voucherType ? voucherType.Name : 'Select voucher';
        const operator = idx === 0 ? '' : ` ${step.operator} `;
        return `${operator}${voucherName}`;
      })
      .join('');
  };

  return (
    <div className="col-span-4 border p-4 bg-gray-50 rounded mt-2">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Stock Calculation Formulas</h3>
          <p className="text-sm text-gray-600">Define custom calculations based on voucher types (e.g., TRY = Sales - Purchase + Transfer In)</p>
        </div>
        <button
          type="button"
          onClick={addFormula}
          className="px-3 py-1 bg-indigo-600 text-white rounded flex items-center hover:bg-indigo-700 transition-colors"
          disabled={loading}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Formula
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-4 bg-blue-50 rounded">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent mr-2"></div>
          <span className="text-sm">Loading voucher types...</span>
        </div>
      )}

      {formulas.length === 0 ? (
        <div className="text-center p-6 bg-blue-50 rounded">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-600">No calculation formulas defined yet.</p>
          <p className="text-sm text-gray-500 mt-1">Click "Add Formula" to create your first calculation.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formulas.map((formula, formulaIdx) => (
            <div key={formulaIdx} className="bg-white p-4 rounded-lg border-2 border-indigo-200 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 mr-4">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Field Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formula.name}
                        onChange={(e) => updateFormulaField(formulaIdx, 'name', e.target.value)}
                        placeholder="e.g., TRY, NET_STOCK, AVAILABLE"
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formula.isActive !== false}
                          onChange={(e) => updateFormulaField(formulaIdx, 'isActive', e.target.checked)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={formula.description || ''}
                      onChange={(e) => updateFormulaField(formulaIdx, 'description', e.target.value)}
                      placeholder="Describe what this calculation represents"
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFormula(formulaIdx)}
                  className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  title="Remove formula"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <div className="border-t pt-3">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-gray-700">Formula Steps</label>
                  <button
                    type="button"
                    onClick={() => addFormulaStep(formulaIdx)}
                    className="text-xs px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
                  >
                    + Add Step
                  </button>
                </div>

                {formula.formula && formula.formula.length > 0 ? (
                  <div className="space-y-2">
                    {formula.formula.sort((a, b) => a.order - b.order).map((step, stepIdx) => (
                      <div key={stepIdx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                        <span className="text-sm font-medium text-gray-500 w-6">{stepIdx + 1}.</span>
                        
                        {stepIdx > 0 && (
                          <select
                            value={step.operator}
                            onChange={(e) => updateFormulaStep(formulaIdx, stepIdx, 'operator', e.target.value)}
                            className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="+">+</option>
                            <option value="-">-</option>
                          </select>
                        )}
                        
                        <select
                          value={step.voucherType}
                          onChange={(e) => updateFormulaStep(formulaIdx, stepIdx, 'voucherType', e.target.value)}
                          className="flex-1 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Select voucher type</option>
                          {voucherTypes.map(vt => (
                            <option key={vt._id} value={vt._id}>{vt.Name}</option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => removeFormulaStep(formulaIdx, stepIdx)}
                          className="px-2 py-1 bg-red-400 text-white rounded hover:bg-red-500 transition-colors"
                          title="Remove step"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic p-2 bg-gray-50 rounded">No steps defined. Click "Add Step" to begin.</p>
                )}

                {formula.formula && formula.formula.length > 0 && (
                  <div className="mt-3 p-2 bg-indigo-50 rounded border border-indigo-200">
                    <span className="text-sm font-semibold text-indigo-800">Preview: </span>
                    <span className="text-sm text-indigo-900 font-mono">{formula.name || 'FIELD_NAME'} = {getFormulaPreview(formula)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const VoucherTypeSelector = ({ companyId, selectedVoucherTypes, onChange }) => {
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [selectedOptions, setSelectedOptions] = useState([]);

  useEffect(() => {
    if (companyId) {
      fetchVoucherTypes();
    }
  }, [companyId]);

  // Convert selected IDs to react-select format when they change
  useEffect(() => {
    if (voucherTypes.length > 0 && selectedVoucherTypes.length > 0) {
      // Convert ID array to react-select format
      const options = selectedVoucherTypes.map(id => {
        const voucherType = voucherTypes.find(vt => vt._id === id);
        return voucherType ? { value: voucherType._id, label: voucherType.Name } : null;
      }).filter(Boolean);
      
      setSelectedOptions(options);
    } else {
      setSelectedOptions([]);
    }
  }, [selectedVoucherTypes, voucherTypes]);

  const fetchVoucherTypes = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tally/voucher-types/${companyId}`);
      if (response.data.success) {
        setVoucherTypes(response.data.data);
      } else {
        toast.error('Failed to fetch voucher types');
      }
    } catch (error) {
      console.error('Error fetching voucher types:', error);
      toast.error('Failed to fetch voucher types');
    } finally {
      setLoading(false);
    }
  };

  // Convert the voucher type data to react-select format
  const options = voucherTypes.map(vt => ({
    value: vt._id,
    label: vt.Name
  }));

  const handleChange = (selectedOptions) => {
    setSelectedOptions(selectedOptions || []);
    // Extract just the IDs to pass back to parent component
    onChange(selectedOptions ? selectedOptions.map(option => option.value) : []);
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#d1d5db',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#9ca3af',
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e0f2fe',
      borderRadius: '0.25rem',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#0369a1',
      fontWeight: 500,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#0369a1',
      '&:hover': {
        backgroundColor: '#bae6fd',
        color: '#0284c7',
      },
    }),
  };

  return (
    <div className="col-span-4 border p-4 bg-gray-50 rounded mt-2">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Supported Voucher Types for Schemes</h3>
      {loading ? (
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
          <span className="ml-2">Loading voucher types...</span>
        </div>
      ) : voucherTypes.length > 0 ? (
        <div>
          <p className="text-sm text-gray-600 mb-2">Select voucher types that can be used for schemes</p>
          <Select
            isMulti
            name="voucherTypes"
            options={options}
            className="basic-multi-select"
            classNamePrefix="select"
            value={selectedOptions}
            onChange={handleChange}
            placeholder="Select voucher types..."
            styles={customStyles}
            closeMenuOnSelect={false}
            isClearable={true}
          />
          {selectedOptions.length > 0 && (
            <p className="mt-2 text-sm text-blue-600">{selectedOptions.length} voucher type(s) selected</p>
          )}
        </div>
      ) : (
        <div className="text-center p-4 bg-blue-50 rounded">
          <p>No voucher types found. Sync voucher types from Tally first.</p>
          <button
            onClick={fetchVoucherTypes}
            className="mt-2 bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};

const DomainView = ({ domain }) => {
  if (!domain) return null;

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
      <h5 className="font-semibold text-lg text-indigo-700 mb-3">
        {domain.domainName}
      </h5>

      <div className="grid grid-cols-1 gap-4">
        <AccessSection 
          label="Admin Dashboard Access" 
          items={domain.adminDashboardAccess || []} 
        />
        <AccessSection 
          label="Company Head Dashboard Access" 
          items={domain.companyHeadDashboardAccess || []} 
        />
        <AccessSection 
          label="Employee Dashboard Access" 
          items={domain.employeeDashboardAccess || []} 
        />
      </div>
    </div>
  );
};

const AccessSection = ({ label, items }) => (
  <div className="border p-3 rounded bg-white">
    <h6 className="font-medium text-gray-700 mb-2">{label}</h6>
    {items.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {items.map(item => (
          <span 
            key={item} 
            className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-md"
          >
            {item.replace(/-/g, ' ')}
          </span>
        ))}
      </div>
    ) : (
      <p className="text-sm italic text-gray-500">No access defined</p>
    )}
  </div>
);



const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editCompany, setEditCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'form'
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCompany, setViewCompany] = useState(null);
  const [tallyCompanies, setTallyCompanies] = useState([]);
  const [selectedTallyName, setSelectedTallyName] = useState('');
  const [selectedTallyGUID, setSelectedTallyGUID] = useState('');
  const [currentActiveCompany, setCurrentActiveCompany] = useState('');
  const [loadingTally, setLoadingTally] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formProgress, setFormProgress] = useState(0);

  const [domains, setDomains] = useState([
    {
      domainName: '',
      adminDashboardAccess: [],
      companyHeadDashboardAccess: [],
      employeeDashboardAccess: [],
    },
  ]);

  const [selectedVoucherTypes, setSelectedVoucherTypes] = useState([]);
  const [selectedDealerRefs, setSelectedDealerRefs] = useState([]);
  const [allDealers, setAllDealers] = useState([]);
  const [quotationTermsAndConditions, setQuotationTermsAndConditions] = useState([]);
  const [deliveryOrderTermsAndConditions, setDeliveryOrderTermsAndConditions] = useState([]);
  const [stockCalculationFormulas, setStockCalculationFormulas] = useState([]);
  const [selectedAdditionalTallyAccess, setSelectedAdditionalTallyAccess] = useState([]);
  const [tallyItemsFieldSettings, setTallyItemsFieldSettings] = useState({});
  const [tallyManagementAccess, setTallyManagementAccess] = useState({
    allowParameterAssignment: true,
    allowBrandAssignment: true,
    allowImageUpload: true,
  });
  const [reorderStatusFieldSettings, setReorderStatusFieldSettings] = useState({});
  const [detailedVoucherTypes, setDetailedVoucherTypes] = useState([]);
  const [includeCancelledInCalculations, setIncludeCancelledInCalculations] = useState(false);

  const createFormRef = useRef(null);
  const editFormRef = useRef(null);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
  });

  // Organized access permissions by categories
  const defaultAdminAccess = [
    // Core Operations
    'delivery-order',
    'items',
    'quotation',
    'crm',
    
    // Tally Integration
    'tally-quotation',
    'tally-items',
    
    // Management & Reports
    'activity-log',
    'campaign-management',
    'schemes-reports',
  ];

  const defaultCompanyHeadAccess = [
    // Dashboard & Core
    'dashboard',
    
    // HR Management
    'employee-management',
    'leave-management',
    'attendance-management',
    'payroll-management',
    'role-management',
    
    // Asset & Brand Management
    'asset-management',
    'brand-management',
    
    // System Management
    'tally-management',
    
    // App Dealer Management
    'app-dealer-management',
    'app-dealer-access-logs',
    'app-dealer-interest-reports',
    
    // Reports & Analytics
    'delivery-orders-reports',
    'activity-logs-reports',
    'choice-entry-reports',
    'quotations-reports', 
    'proforma-invoice-reports',
    'delivery-orders-detailed-reports',
    'showroom-analytics',
    
    // Financial
    'tiles-day-book',
    'tiles-ledger',
    
    // Tiles Management
    'tiles-management',
    
    // Automations
    'whatsapp-automations',
  ];

  const defaultEmployeeAccess = [
    // Dashboard & Core
    'dashboard',
    
    // Core Operations
    'delivery-order',
    'quotation',
    'quotation-new',
    'estimate',
    'proforma-invoice',
    'packing-slip',
    'goods-receipt',
    
    // Customer & Sales Management
    'tele-calling-sales',
    'tele-calling-payment',
    'customer-management',
    'dealer-management',
    'buyers',
    'supplier-management',
    'guest-master',
    'reference-management',
    'ma-management',
    
    // Inventory & Items
    'item-management',
    'purchase-indent',
    'reference-commission',
    
    // HR & Personal
    'apply-leave',
    'attendance',
    'field-visit',
    'reminders',
    
    // Activity & Logs
    'activity-log',
    'activity-logs-new',
    'choice-entry',
    
    // Location & Transport
    'station-management',
    'transport-management',
    
    // Doors Management
    'doors',
    'doors-inward',
    'doors-outward',
    'doors-stock-status',
    'doors-parameters',
    
    // Tiles Management
    'tiles-master',
    'tiles-parameters',
    'tiles-stock-status',
    'tiles-inward',
    'tiles-outward',
    'tiles-delivery-order',
    'tiles-sales-order',
    'tiles-location-transfer',
    'tiles-day-book',
    'tiles-ledger',
    'tiles-order',
    'tiles-dispatch-entry',
    'tiles-received',
    
    // Tally Integration
    'tally-quotation',
    'tally-items-management',
    'tally-parameters-management',
    'tally-ledger-management',
    
    // Stock Management
    'stock-master',
    'stock-items-inward',
    'stock-items-outward',
    'stock-items-location-transfer',
    'stock-items-status',
    'stock-items-ledger',
    'stock-items-day-book',
    'stock-movement',
    'stock-items-movement',
    'stock-items-order',
    'stock-items-dispatch',
    'stock-items-received',
    'stock-items-destroy',
    
    // Product & Inventory
    'concept-product',
    'demolished-tiles',
    'tiles-return',
    'tally-operations-panel',
  ];

  const [adminAccess, setAdminAccess] = useState([...defaultAdminAccess]);
  const [companyHeadAccess, setCompanyHeadAccess] = useState([
    ...defaultCompanyHeadAccess,
  ]);
  const [employeeAccess, setEmployeeAccess] = useState([
    ...defaultEmployeeAccess,
  ]);

  useEffect(() => {
    if (editCompany) {
      setAdminAccess(editCompany.adminDashboardAccess || []);
      setCompanyHeadAccess(editCompany.companyHeadDashboardAccess || []);
      setEmployeeAccess(editCompany.employeeDashboardAccess || []);

      setSelectedVoucherTypes(
        editCompany.supportedVoucherTypes 
          ? editCompany.supportedVoucherTypes.map(vt => vt._id || vt) 
          : []
      );

      setQuotationTermsAndConditions(editCompany.quotationTermsAndConditions || []);
      setDeliveryOrderTermsAndConditions(editCompany.deliveryOrderTermsAndConditions || []);
      setStockCalculationFormulas(editCompany.stockCalculationFormulas || []);

      // Initialize dealer references
      setSelectedDealerRefs(
        editCompany.dealerReferences
          ? editCompany.dealerReferences.map(d => d._id || d)
          : []
      );
      fetchDealers();

      // Initialize additional tally items access
      setSelectedAdditionalTallyAccess(
        editCompany.additionalTallyItemsAccess
          ? editCompany.additionalTallyItemsAccess.map(c => c._id || c)
          : []
      );

      // Initialize tally field settings
      setTallyItemsFieldSettings(editCompany.tallyItemsFieldSettings || {});
      setTallyManagementAccess(editCompany.tallyManagementAccess || {
        allowParameterAssignment: true,
        allowBrandAssignment: true,
        allowImageUpload: true,
      });
      setReorderStatusFieldSettings(editCompany.reorderStatusFieldSettings || {});
      setDetailedVoucherTypes(
        editCompany.detailedVoucherTypes
          ? editCompany.detailedVoucherTypes.map(vt => vt._id || vt)
          : []
      );
      setIncludeCancelledInCalculations(editCompany.includeCancelledInCalculations || false);

      if (editCompany.domains && editCompany.domains.length > 0) {
        setDomains(editCompany.domains);
      } else {
        setDomains([
          {
            domainName: editCompany.domainName || '',
            adminDashboardAccess: editCompany.adminDashboardAccess || [],
            companyHeadDashboardAccess:
              editCompany.companyHeadDashboardAccess || [],
            employeeDashboardAccess: editCompany.employeeDashboardAccess || [],
          },
        ]);
      }

      if (editCompany.TallyCompany) {
        setSelectedTallyName(editCompany.TallyCompany);
      }
      if (editCompany.companyGUID) {
        setSelectedTallyGUID(editCompany.companyGUID);
      }

      // Reset form errors and calculate initial progress
      setFormErrors({});
      setTimeout(() => {
        calculateFormProgress();
      }, 200);
    } else {
      setAdminAccess([...defaultAdminAccess]);
      setCompanyHeadAccess([...defaultCompanyHeadAccess]);
      setEmployeeAccess([...defaultEmployeeAccess]);
      setSelectedVoucherTypes([]);
      setQuotationTermsAndConditions([]);
      setDeliveryOrderTermsAndConditions([]);
      setStockCalculationFormulas([]);
      setSelectedDealerRefs([]);
      setSelectedAdditionalTallyAccess([]);
      setTallyItemsFieldSettings({});
      setTallyManagementAccess({ allowParameterAssignment: true, allowBrandAssignment: true, allowImageUpload: true });
      setReorderStatusFieldSettings({});
      setDetailedVoucherTypes([]);
      setIncludeCancelledInCalculations(false);

      setDomains([
        {
          domainName: '',
          adminDashboardAccess: [...defaultAdminAccess],
          companyHeadDashboardAccess: [...defaultCompanyHeadAccess],
          employeeDashboardAccess: [...defaultEmployeeAccess],
        },
      ]);
    }
  }, [editCompany]);

  // Fetch all dealers for dealer references multi-select
  const fetchDealers = async (filter = '') => {
    try {
      const res = await api.get('/dealers', { params: { filter, limit: 500, page: 1 } });
      setAllDealers((res.data.dealers || []).map(d => ({ value: d._id, label: `${d.businessName} (${d.phone || 'N/A'})` })));
    } catch (err) {
      console.error('Error fetching dealers:', err);
    }
  };

  const checkTallyActive = async (tallyUrl) => {
    setLoadingTally(true);
    try {
      const res = await api.post('/tally/is-tally-active', { tallyUrl });
      const isActive = res.data.active;
      if (isActive) {
        toast.success('✅ Tally server is active and responding');
      } else {
        toast.error('❌ Tally server is not responding');
      }
      return isActive;
    } catch (err) {
      toast.error('❌ Error checking Tally server status');
      Swal.fire({ icon: 'error', title: 'Error', text: 'Error checking if Tally is active.' });
      return false;
    } finally {
      setLoadingTally(false);
    }
  };

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/companies`
      );
      setCompanies(response.data);
      setFilteredCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies.');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch companies.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Search and filter functionality
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    const filtered = companies.filter(company =>
      company.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      company.email.toLowerCase().includes(searchValue.toLowerCase()) ||
      (company.firm && company.firm.firmName.toLowerCase().includes(searchValue.toLowerCase()))
    );
    setFilteredCompanies(filtered);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleEditClick = async (company) => {
    const tallyUrl = company.tallyUrl;
    if (tallyUrl && tallyUrl !== '') {
      const isActive = await checkTallyActive(tallyUrl);
      if (!isActive) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Tally is not active for this company. Unable to edit.' });
        toast.warning("Tally is not active. Unable to edit.");
        setActiveTab('list');
        return;
      } else {
        setLoadingTally(true);
        try {
          const listRes = await api.post('/tally/get-tally-companies', { tallyUrl });
          const list = listRes.data.response || [];
          setTallyCompanies(list);

          if (company.companyGUID) {
            const compInTally = list.find(comp => comp.GUID === company.companyGUID);
            if (compInTally) {
              setSelectedTallyName(compInTally.NAME);
              setSelectedTallyGUID(compInTally.GUID);
            }
          }

          const currentRes = await api.post('/tally/get-current-active-company', { tallyUrl });
          setCurrentActiveCompany(currentRes.data.response);
        } catch (error) {
          console.error('Error fetching tally data:', error);
          toast.error('Failed to fetch tally data.');
        }
        setLoadingTally(false);
      }
    }

    setEditCompany(company);
    setActiveTab('form');
  };

  // Enhanced form validation with detailed error messages
  const validateForm = (event) => {
    const form = new FormData(event.target);
    const errors = {};
    let isValid = true;

    // Required field validation
    const requiredFields = {
      name: 'Company Name',
      email: 'Email',
      printName: 'Print Name',
      address: 'Address',
      phone: 'Phone',
      bankName: 'Bank Name',
      branchName: 'Branch Name',
      accountNo: 'Account Number',
      ifsc: 'IFSC Code',
      tallyUrl: 'Tally URL'
    };

    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!form.get(field)) {
        errors[field] = `${label} is required`;
        isValid = false;
      }
    });

    // Email validation
    const email = form.get('email');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // IFSC validation
    const ifsc = form.get('ifsc');
    if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      errors.ifsc = 'Please enter a valid IFSC code (e.g., SBIN0001234)';
      isValid = false;
    }

    // Tally URL validation
    const tallyUrl = form.get('tallyUrl');
    if (tallyUrl && !/^https?:\/\/.+/.test(tallyUrl)) {
      errors.tallyUrl = 'Please enter a valid URL (e.g., http://localhost:9000)';
      isValid = false;
    }

    setFormErrors(errors);

    if (!isValid) {
      toast.error('Please fix the errors in the form before submitting');
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }

    return isValid;
  };

  // Calculate form progress
  const calculateFormProgress = () => {
    if (!editFormRef.current) return 0;
    
    const form = new FormData(editFormRef.current);
    const requiredFields = ['name', 'email', 'printName', 'address', 'phone', 'bankName', 'branchName', 'accountNo', 'ifsc', 'tallyUrl'];
    const filledFields = requiredFields.filter(field => form.get(field));
    const progress = Math.round((filledFields.length / requiredFields.length) * 100);
    
    setFormProgress(progress);
    return progress;
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm(event)) return;

    for (let domain of domains) {
      if (!domain.domainName) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Each domain must have a name.',
        });
        return;
      }
    }

    const formData = new FormData(event.target);

    console.log('=== FORM SUBMIT DEBUG ===');
    console.log('Selected Tally Name before submit:', selectedTallyName);
    console.log('Selected Tally GUID before submit:', selectedTallyGUID);
    console.log('FormData initial TallyCompany:', formData.get('TallyCompany'));
    console.log('FormData initial companyGUID:', formData.get('companyGUID'));
    
    // Force set the values from state
    formData.set('TallyCompany', selectedTallyName || '');
    formData.set('companyGUID', selectedTallyGUID || '');

    formData.set('supportedVoucherTypes', JSON.stringify(selectedVoucherTypes));
    formData.set('stockCalculationFormulas', JSON.stringify(stockCalculationFormulas));
    formData.set('quotationTermsAndConditions', JSON.stringify(quotationTermsAndConditions));
    formData.set('deliveryOrderTermsAndConditions', JSON.stringify(deliveryOrderTermsAndConditions));
    formData.set('dealerReferences', JSON.stringify(selectedDealerRefs));
    formData.set('additionalTallyItemsAccess', JSON.stringify(selectedAdditionalTallyAccess));
    formData.set('tallyItemsFieldSettings', JSON.stringify(tallyItemsFieldSettings));
    formData.set('tallyManagementAccess', JSON.stringify(tallyManagementAccess));
    formData.set('reorderStatusFieldSettings', JSON.stringify(reorderStatusFieldSettings));
    formData.set('detailedVoucherTypes', JSON.stringify(detailedVoucherTypes));
    formData.set('includeCancelledInCalculations', includeCancelledInCalculations ? 'true' : 'false');
    console.log('FormData AFTER set - TallyCompany:', formData.get('TallyCompany'));
    console.log('FormData AFTER set - companyGUID:', formData.get('companyGUID'));
    console.log('=========================');

    const tallyFields = [
      'ADMINMAILID',
      'APPLICATIONPATH',
      'DATAPATH',
      'ISLICCLIENTMODE',
      'ISREMOTEACCESSMODE',
      'SERIALNUMBER',
    ];

    tallyFields.forEach((field) => {
      formData.set(field, editCompany[field] || '');
    });

    if (!formData.has('isStockStatus')) {
      formData.append('isStockStatus', 'false');
    }
    
    if (!formData.has('isTallyMaestroEnabled')) {
      formData.append('isTallyMaestroEnabled', 'false');
    }

    if (!formData.has('isChoicemicEnabled')) {
      formData.append('isChoicemicEnabled', 'false');
    }

    formData.set('domainsData', JSON.stringify(domains));

    setLoadingSubmit(true);
    try {
      const response = await api.put(`/companies/${editCompany._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      console.log('=== COMPANY UPDATE RESPONSE ===');
      console.log('Updated company data:', response.data);
      console.log('TallyCompany in response:', response.data.TallyCompany);
      console.log('companyGUID in response:', response.data.companyGUID);
      
      // Clear form errors on success
      setFormErrors({});
      
      // Show success message with SweetAlert
      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Company information has been updated successfully.',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
      
      toast.success('Company updated successfully.');
      
      // Refresh companies list
      await fetchCompanies();
      
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating company:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to update company.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check your input.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      toast.error(errorMessage);
      
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMessage,
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleViewTallyDetails = async () => {
    setLoadingTally(true);
    
    // Show loading animation
    Swal.fire({
      title: 'Verifying Tally Connection',
      html: `
        <div class="flex flex-col items-center space-y-4">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
          <p class="text-gray-600 animate-pulse">Connecting to Tally server...</p>
          <p class="text-sm text-gray-500">Please wait while we verify the connection</p>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      customClass: {
        popup: 'animate-fadeInUp'
      }
    });

    try {
      const licenceRes = await api.post('/tally/get-lincence', { tallyUrl: editCompany.tallyUrl });
      const licenceInfo = licenceRes.data.response;

      setEditCompany(prev => ({
        ...prev,
        ADMINMAILID: licenceInfo.ADMINMAILID,
        APPLICATIONPATH: licenceInfo.APPLICATIONPATH,
        DATAPATH: licenceInfo.DATAPATH,
        ISLICCLIENTMODE: licenceInfo.ISLICCLIENTMODE === "Yes",
        ISREMOTEACCESSMODE: licenceInfo.ISREMOTEACCESSMODE === "Yes",
        SERIALNUMBER: licenceInfo.SERIALNUMBER,
      }));

      let licenceHtml = '<table style="width:100%; border-collapse:collapse; font-size: 12px;">';
      Object.keys(licenceInfo).forEach(key => {
        licenceHtml += `<tr style="border-bottom:1px solid #e5e7eb;"><td style="padding:6px 8px; font-weight:600; background:#f9fafb; color:#374151;">${key}</td><td style="padding:6px 8px; color:#6b7280;">${licenceInfo[key]}</td></tr>`;
      });
      licenceHtml += '</table>';

      // Show success with animation
      setTimeout(() => {
        Swal.fire({
          title: '<span style="color:#059669;">✅ Tally Verification Successful</span>',
          html: `
            <div class="text-left">
              <div class="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p class="text-sm font-semibold text-green-800">Selected Tally Company: ${selectedTallyName}</p>
              </div>
              <div class="max-h-80 overflow-y-auto">
                ${licenceHtml}
              </div>
            </div>
          `,
          width: '700px',
          customClass: {
            popup: 'animate-fadeInUp',
            title: 'text-lg',
            content: 'text-sm'
          },
          showClass: {
            popup: 'animate__animated animate__fadeInUp'
          }
        });
      }, 800);
    } catch (error) {
      console.error('Error fetching license details:', error);
      
      // Show error with animation
      setTimeout(() => {
        Swal.fire({
          title: '<span style="color:#dc2626;">❌ Tally Verification Failed</span>',
          html: `
            <div class="text-center">
              <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <svg class="w-12 h-12 text-red-500 mx-auto mb-2 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <p class="text-sm font-semibold text-red-800 mb-2">Connection Failed</p>
                <p class="text-sm text-red-600">Unable to connect to Tally server. Please check:</p>
                <ul class="text-xs text-red-600 mt-2 space-y-1">
                  <li>• Tally server is running</li>
                  <li>• URL is correct</li>
                  <li>• Network connectivity</li>
                </ul>
              </div>
            </div>
          `,
          customClass: {
            popup: 'animate-shakeX',
            title: 'text-lg'
          },
          showClass: {
            popup: 'animate__animated animate__shakeX'
          }
        });
      }, 500);
      
      toast.error('Failed to verify Tally connection');
    }
    setLoadingTally(false);
  };

  const renderTallyControls = () => (
    <div className="col-span-4 border p-3 bg-gray-50 rounded mt-2 animate-fadeInUp shadow-sm hover:shadow-md transition-all duration-300">
      <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
        <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Tally Integration
      </h3>
      <div className="flex items-center space-x-2 flex-wrap gap-y-2">
        <button
          type="button"
          onClick={async () => {
            setLoadingTally(true);
            try {
              const listRes = await api.post('/tally/get-tally-companies', { tallyUrl: editCompany.tallyUrl });
              const list = listRes.data.response || [];
              setTallyCompanies(list);
              const currentRes = await api.post('/tally/get-current-active-company', { tallyUrl: editCompany.tallyUrl });
              setCurrentActiveCompany(currentRes.data.response);
            } catch (error) {
              console.error('Error fetching tally companies:', error);
              
              Swal.fire({
                title: '<span style="color:#dc2626;">⚠️ Connection Error</span>',
                html: `
                  <div class="text-center">
                    <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                      <svg class="w-10 h-10 text-red-500 mx-auto mb-2 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <p class="text-sm font-semibold text-red-800">Failed to fetch Tally companies</p>
                      <p class="text-xs text-red-600 mt-1">Please verify Tally server connection</p>
                    </div>
                  </div>
                `,
                customClass: {
                  popup: 'animate-shakeX'
                },
                timer: 3000,
                timerProgressBar: true
              });
              
              toast.error('Failed to fetch Tally companies');
            }
            setLoadingTally(false);
          }}
          className={`bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md flex items-center text-xs font-medium transition-all duration-200 transform hover:scale-105 shadow-sm ${
            loadingTally ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-md'
          }`}
          disabled={loadingTally}
        >
          {loadingTally ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-2"></div>
              <span className="animate-pulse">Verifying...</span>
            </>
          ) : (
            <>
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Get Companies
            </>
          )}
        </button>

        {tallyCompanies.length > 0 && (
          <div className="flex-grow animate-slideInRight">
            <select
              value={selectedTallyGUID}
              onChange={(e) => {
                const guid = e.target.value;
                setSelectedTallyGUID(guid);
                const comp = tallyCompanies.find(c => c.GUID === guid);
                if (comp) {
                  setSelectedTallyName(comp.NAME);
                }
              }}
              className="border border-gray-300 p-1.5 w-full md:w-80 rounded-md text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-white hover:border-purple-400"
            >
              <option value="">Select Tally Company</option>
              {tallyCompanies.map((comp, idx) => {
                const optionValue = comp.GUID;
                const isCurrent = currentActiveCompany && optionValue === currentActiveCompany;
                return (
                  <option key={idx} value={optionValue} className="text-xs">
                    {comp.NAME} {isCurrent ? '(Current)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {tallyCompanies.length > 0 && (
          <button
            type="button"
            onClick={handleViewTallyDetails}
            className={`bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 transform hover:scale-105 shadow-sm animate-slideInLeft ${
              loadingTally ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-md'
            }`}
            disabled={loadingTally}
          >
            {loadingTally ? (
              <span className="animate-pulse">Loading...</span>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Details
              </>
            )}
          </button>
        )}
      </div>

      {selectedTallyName && (
        <div className="mt-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md animate-fadeIn">
          <p className="text-blue-800 text-xs flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Selected: <span className="font-semibold ml-1">{selectedTallyName}</span>
          </p>
        </div>
      )}
    </div>
  );

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm(event)) return;

    for (let domain of domains) {
      if (!domain.domainName) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Each domain must have a name.',
        });
        return;
      }
    }

    const formData = new FormData(event.target);

    if (!formData.has('isStockStatus')) {
      formData.append('isStockStatus', 'false');
    }
    
    if (!formData.has('isTallyMaestroEnabled')) {
      formData.append('isTallyMaestroEnabled', 'false');
    }

    if (!formData.has('isChoicemicEnabled')) {
      formData.append('isChoicemicEnabled', 'false');
    }

    formData.set('domainsData', JSON.stringify(domains));
    formData.set('supportedVoucherTypes', JSON.stringify(selectedVoucherTypes));
    formData.set('stockCalculationFormulas', JSON.stringify(stockCalculationFormulas));
    formData.set('quotationTermsAndConditions', JSON.stringify(quotationTermsAndConditions));
    formData.set('deliveryOrderTermsAndConditions', JSON.stringify(deliveryOrderTermsAndConditions));
    formData.set('dealerReferences', JSON.stringify(selectedDealerRefs));
    formData.set('additionalTallyItemsAccess', JSON.stringify(selectedAdditionalTallyAccess));
    formData.set('tallyItemsFieldSettings', JSON.stringify(tallyItemsFieldSettings));
    formData.set('tallyManagementAccess', JSON.stringify(tallyManagementAccess));
    formData.set('reorderStatusFieldSettings', JSON.stringify(reorderStatusFieldSettings));
    formData.set('detailedVoucherTypes', JSON.stringify(detailedVoucherTypes));
    formData.set('includeCancelledInCalculations', includeCancelledInCalculations ? 'true' : 'false');

    setLoadingSubmit(true);
    try {
      await api.post('/companies', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Company created successfully.');
      fetchCompanies();
      handleCloseCreateDialog();
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Failed to create company.');
    }
    setLoadingSubmit(false);
  };

  const handleCloseEditDialog = () => {
    setEditCompany(null);
    setTallyCompanies([]);
    setSelectedTallyName('');
    setSelectedTallyGUID('');
    setCurrentActiveCompany('');
    setActiveTab('list');
  };

  const handleCloseCreateDialog = () => {
    setEditCompany(null);
    setActiveTab('list');
  };

  const handleViewClick = (company) => {
    setViewCompany(company);
    setViewModalOpen(true);
  };

  return (
    <div
      className="p-6 animate-fadeIn container mx-auto"
      style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('form')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'form'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Create/Edit Company</span>
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Companies List</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'form' && (
        <div className="animate-fadeIn">
          {/* Create/Edit Company Form */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {editCompany ? 'Edit Company' : 'Create New Company'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {editCompany ? 'Update company information and settings' : 'Add a new company to the system'}
                  </p>
                </div>
              </div>
              {editCompany && (
                <button
                  onClick={() => {
                    setEditCompany(null);
                    setActiveTab('list');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Company Form */}
            <form 
              className="space-y-6"
              onSubmit={editCompany ? handleEditSubmit : handleCreateSubmit}
              ref={editCompany ? editFormRef : createFormRef}
            >
              {/* Error Summary */}
              {Object.keys(formErrors).length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-red-800 font-semibold">Please fix the following errors:</h4>
                  </div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {Object.entries(formErrors).map(([field, error]) => (
                      <li key={field} className="flex items-center">
                        <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Basic Company Information */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  Company Information
                  <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Company Name *</label>
                    <input
                      name="name"
                      defaultValue={editCompany?.name || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Email *</label>
                    <input
                      name="email"
                      type="email"
                      defaultValue={editCompany?.email || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Print Name *</label>
                    <input
                      name="printName"
                      defaultValue={editCompany?.printName || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter print name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Phone *</label>
                    <input
                      name="phone"
                      defaultValue={editCompany?.phone || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block font-medium text-gray-700">Address *</label>
                    <textarea
                      name="address"
                      defaultValue={editCompany?.address || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      rows="2"
                      placeholder="Enter company address"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">GST Number</label>
                    <input
                      name="gstNo"
                      defaultValue={editCompany?.gstNo || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter GST number (optional)"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isStockStatus"
                        defaultChecked={editCompany?.isStockStatus || false}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="block font-medium text-gray-700">Enable Stock Management</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isTallyMaestroEnabled"
                        defaultChecked={editCompany?.isTallyMaestroEnabled || false}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="block font-medium text-gray-700">Enable Tally Maestro App</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isChoicemicEnabled"
                        defaultChecked={editCompany?.isChoicemicEnabled || false}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="block font-medium text-gray-700">Enable ChoiceMic App</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={includeCancelledInCalculations}
                        onChange={(e) => setIncludeCancelledInCalculations(e.target.checked)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="block font-medium text-gray-700">Include Cancelled in Calculations</span>
                    </label>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block font-medium text-gray-700">Additional Tally Items Access</label>
                    <Select
                      isMulti
                      options={companies
                        .filter(c => c._id !== editCompany?._id)
                        .map(c => ({ value: c._id, label: `${c.name} (${c.id || ''})` }))}
                      value={companies
                        .filter(c => selectedAdditionalTallyAccess.includes(c._id))
                        .map(c => ({ value: c._id, label: `${c.name} (${c.id || ''})` }))}
                      onChange={(selected) => setSelectedAdditionalTallyAccess(selected ? selected.map(s => s.value) : [])}
                      placeholder="Select sibling companies for tally items cross-access..."
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isClearable
                    />
                    <p className="text-xs text-gray-500">Employees of this company can view tally items from selected companies in Tally Maestro</p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block font-medium text-gray-700">Dealer References</label>
                    <Select
                      isMulti
                      options={allDealers}
                      value={allDealers.filter(d => selectedDealerRefs.includes(d.value))}
                      onChange={(selected) => setSelectedDealerRefs(selected ? selected.map(s => s.value) : [])}
                      placeholder="Search and select dealers..."
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isClearable
                      onInputChange={(val) => { if (val.length >= 2) fetchDealers(val); }}
                    />
                  </div>
                </div>
              </div>

              {/* Banking Information */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  Banking Details
                  <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Bank Name *</label>
                    <input
                      name="bankName"
                      defaultValue={editCompany?.bankName || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Branch Name *</label>
                    <input
                      name="branchName"
                      defaultValue={editCompany?.branchName || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter branch name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Account Number *</label>
                    <input
                      name="accountNo"
                      defaultValue={editCompany?.accountNo || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter account number"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">IFSC Code *</label>
                    <input
                      name="ifsc"
                      defaultValue={editCompany?.ifsc || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    />
                  </div>
                </div>
              </div>

              {/* File Uploads */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-purple-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  File Uploads
                  <span className="ml-auto text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">{editCompany ? 'Optional' : 'Required'}</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Company Logo {!editCompany && '*'}</label>
                    <input
                      type="file"
                      name="logo"
                      accept="image/*"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required={!editCompany}
                    />
                    {editCompany && (
                      <p className="text-xs text-gray-500">Leave empty to keep existing logo</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Bank QR Code {!editCompany && '*'}</label>
                    <input
                      type="file"
                      name="bankQR"
                      accept="image/*"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required={!editCompany}
                    />
                    <input
                      type="text"
                      name="qrName"
                      defaultValue={editCompany?.qrName || ''}
                      placeholder="QR Code name/description"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-2"
                    />
                    {editCompany && (
                      <p className="text-xs text-gray-500">Leave empty to keep existing QR code</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-yellow-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    </svg>
                  </div>
                  Social Media & Website
                  <span className="ml-auto text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Optional</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Website URL</label>
                    <input
                      name="website"
                      type="url"
                      defaultValue={editCompany?.website || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://www.example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">LinkedIn URL</label>
                    <input
                      name="linkedin"
                      type="url"
                      defaultValue={editCompany?.linkedin || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://linkedin.com/company/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Facebook URL</label>
                    <input
                      name="facebook"
                      type="url"
                      defaultValue={editCompany?.facebook || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://facebook.com/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Instagram URL</label>
                    <input
                      name="instagram"
                      type="url"
                      defaultValue={editCompany?.instagram || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://instagram.com/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Twitter URL</label>
                    <input
                      name="twitter"
                      type="url"
                      defaultValue={editCompany?.twitter || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="https://twitter.com/example"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">WhatsApp Business</label>
                    <input
                      name="whatsappBusiness"
                      defaultValue={editCompany?.whatsappBusiness || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="WhatsApp Business number or link"
                    />
                  </div>
                </div>
              </div>

              {/* Tally Integration */}
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-6 h-6 bg-indigo-500 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  Tally Integration
                  <span className="ml-auto text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="block font-medium text-gray-700">Tally URL *</label>
                    <input
                      name="tallyUrl"
                      defaultValue={editCompany?.tallyUrl || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                      placeholder="http://localhost:9000 or http://your-tally-server:9000"
                    />
                  </div>
                  
                  {/* Tally Controls - Only show if editing */}
                  {editCompany && renderTallyControls()}
                  
                  {/* Hidden fields for Tally data */}
                  <input type="hidden" name="TallyCompany" value={selectedTallyName} />
                  <input type="hidden" name="companyGUID" value={selectedTallyGUID} />
                  <input type="hidden" name="ADMINMAILID" value={editCompany?.ADMINMAILID || ''} />
                  <input type="hidden" name="APPLICATIONPATH" value={editCompany?.APPLICATIONPATH || ''} />
                  <input type="hidden" name="DATAPATH" value={editCompany?.DATAPATH || ''} />
                  <input type="hidden" name="ISLICCLIENTMODE" value={editCompany?.ISLICCLIENTMODE ? 'on' : 'off'} />
                  <input type="hidden" name="ISREMOTEACCESSMODE" value={editCompany?.ISREMOTEACCESSMODE ? 'on' : 'off'} />
                  <input type="hidden" name="SERIALNUMBER" value={editCompany?.SERIALNUMBER || ''} />
                </div>
              </div>

              {/* Tally Maestro Settings */}
              {editCompany && (
                <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-6 h-6 bg-cyan-500 rounded-md flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    Tally Items Field Settings
                    <span className="ml-auto text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full">Display</span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[
                      { key: 'showCategory', label: 'Category' },
                      { key: 'showBrand', label: 'Brand' },
                      { key: 'showHsnCode', label: 'HSN Code' },
                      { key: 'showPartNo', label: 'Part No' },
                      { key: 'showBaseUnits', label: 'Base Units' },
                      { key: 'showAdditionalUnits', label: 'Additional Units' },
                      { key: 'showStockSummary', label: 'Stock Summary' },
                      { key: 'showCalculatedStock', label: 'Calculated Stock' },
                      { key: 'showPricing', label: 'Pricing' },
                      { key: 'showParameterPricing', label: 'Parameter Pricing' },
                      { key: 'showParameters', label: 'Parameters' },
                      { key: 'showDimensions', label: 'Dimensions' },
                      { key: 'showCoverage', label: 'Coverage' },
                      { key: 'showPiecesPerBox', label: 'Pieces Per Box' },
                      { key: 'showTaxInfo', label: 'Tax Info' },
                      { key: 'showGstDetails', label: 'GST Details' },
                      { key: 'showTechnicalDetails', label: 'Technical Details' },
                      { key: 'showGuid', label: 'GUID' },
                      { key: 'showAlterId', label: 'AlterID' },
                      { key: 'showParent', label: 'Parent' },
                      { key: 'showSection', label: 'Section' },
                      { key: 'showTimestamps', label: 'Timestamps' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md border border-gray-200 hover:border-cyan-300 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tallyItemsFieldSettings[key] !== undefined ? tallyItemsFieldSettings[key] : true}
                          onChange={(e) => setTallyItemsFieldSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Tally Management Access */}
              {editCompany && (
                <div className="bg-violet-50 p-4 rounded-lg border border-violet-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-6 h-6 bg-violet-500 rounded-md flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    Tally Management Access Controls
                    <span className="ml-auto text-xs bg-violet-100 text-violet-800 px-2 py-1 rounded-full">Permissions</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: 'allowParameterAssignment', label: 'Allow Parameter Assignment' },
                      { key: 'allowBrandAssignment', label: 'Allow Brand Assignment' },
                      { key: 'allowImageUpload', label: 'Allow Image Upload' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center space-x-2 bg-white px-4 py-3 rounded-md border border-gray-200 hover:border-violet-300 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tallyManagementAccess[key] !== undefined ? tallyManagementAccess[key] : true}
                          onChange={(e) => setTallyManagementAccess(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Reorder Status Field Settings */}
              {editCompany && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-6 h-6 bg-amber-500 rounded-md flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    Reorder Status Field Settings
                    <span className="ml-auto text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">Stock</span>
                  </h4>
                  <div className="space-y-3">
                    {[
                      { prefix: 'purchaseOrder', label: 'Purchase Order' },
                      { prefix: 'salesOrderDue', label: 'Sales Order Due' },
                      { prefix: 'netAvailable', label: 'Net Available' },
                      { prefix: 'reorderLevel', label: 'Reorder Level' },
                      { prefix: 'shortFall', label: 'Short Fall' },
                      { prefix: 'minimumReorderQty', label: 'Minimum Reorder Qty' },
                      { prefix: 'orderToBePlaced', label: 'Order To Be Placed' },
                    ].map(({ prefix, label }) => {
                      const showKey = `show${prefix.charAt(0).toUpperCase() + prefix.slice(1)}`;
                      const nameKey = `${prefix}DisplayName`;
                      const sqftKey = `${prefix}UseSqft`;
                      return (
                        <div key={prefix} className="flex items-center gap-4 bg-white px-4 py-2 rounded-md border border-gray-200">
                          <label className="flex items-center space-x-2 min-w-[160px]">
                            <input
                              type="checkbox"
                              checked={reorderStatusFieldSettings[showKey] || false}
                              onChange={(e) => setReorderStatusFieldSettings(prev => ({ ...prev, [showKey]: e.target.checked }))}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{label}</span>
                          </label>
                          <input
                            type="text"
                            value={reorderStatusFieldSettings[nameKey] || label}
                            onChange={(e) => setReorderStatusFieldSettings(prev => ({ ...prev, [nameKey]: e.target.value }))}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-amber-500"
                            placeholder="Display name"
                          />
                          <label className="flex items-center space-x-1">
                            <input
                              type="checkbox"
                              checked={reorderStatusFieldSettings[sqftKey] || false}
                              onChange={(e) => setReorderStatusFieldSettings(prev => ({ ...prev, [sqftKey]: e.target.checked }))}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="text-xs text-gray-600">Sqft</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Domain Configuration */}
              {(editCompany || !editCompany) && (
                <DomainAccessManager 
                  domains={domains} 
                  setDomains={setDomains} 
                  defaultAccess={{
                    admin: defaultAdminAccess,
                    companyHead: defaultCompanyHeadAccess,
                    employee: defaultEmployeeAccess
                  }} 
                />
              )}

              {/* Voucher Types */}
              {editCompany && (
                <VoucherTypeSelector 
                  companyId={editCompany._id}
                  selectedVoucherTypes={selectedVoucherTypes}
                  onChange={setSelectedVoucherTypes}
                />
              )}

              {/* Stock Calculation Formulas */}
              {editCompany && (
                <StockCalculationFormulaManager
                  companyId={editCompany._id}
                  formulas={stockCalculationFormulas}
                  setFormulas={setStockCalculationFormulas}
                />
              )}

              {/* Terms and Conditions */}
              <TermsAndConditionsManager
                title="Quotation Terms and Conditions"
                terms={quotationTermsAndConditions}
                setTerms={setQuotationTermsAndConditions}
                placeholder="Enter quotation term or condition"
              />

              <TermsAndConditionsManager
                title="Delivery Order Terms and Conditions"
                terms={deliveryOrderTermsAndConditions}
                setTerms={setDeliveryOrderTermsAndConditions}
                placeholder="Enter delivery order term or condition"
              />

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setEditCompany(null);
                    setActiveTab('list');
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editCompany ? 'Update Company' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="animate-fadeIn">
          {/* Header for List Tab */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Companies List</h3>
                <p className="text-sm text-gray-600">View and manage all companies</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditCompany(null);
                setActiveTab('form');
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Create Company</span>
            </button>
          </div>

          {/* Loading State with Better Animation */}
      {isLoading && (
        <div className="w-full text-center p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl mb-6 animate-pulse">
          <div className="flex items-center justify-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-blue-200 rounded-full"></div>
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-blue-700">
              <p className="font-medium text-lg">Loading companies...</p>
              <p className="text-sm opacity-75">Please wait while we fetch the data</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      {!isLoading && companies.length > 0 && (
        <div className="mb-4 animate-slideUp">
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search companies by name, email, or firm..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md text-sm"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => handleSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              Found {filteredCompanies.length} of {companies.length} companies
            </div>
          )}
        </div>
      )}

      {/* Enhanced Table with Better Styling */}
      <div className="overflow-hidden rounded-xl shadow-lg border border-gray-200 animate-slideUp">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            {/* Enhanced Header */}
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Logo</span>
                  </div>
                </th>
                <th className="group px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>Company Name</span>
                  </div>
                </th>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <span>Email</span>
                  </div>
                </th>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Firm</span>
                  </div>
                </th>
                <th className="group px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 hover:bg-gray-200 transition-colors duration-200">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            
            {/* Enhanced Body with Staggered Animation */}
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredCompanies.map((company, index) => (
                <tr
                  key={company._id}
                  className="group hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 transform hover:scale-[1.01] animate-fadeInUp border-l-4 border-transparent hover:border-blue-500"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  {/* Logo Column with Enhanced Styling */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="relative group">
                      <div className="w-12 h-12 rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-300 border-2 border-gray-200 group-hover:border-blue-300">
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/companies/image/${company._id}`}
                          alt={`${company.name} logo`}
                          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/50?text=Logo';
                          }}
                        />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </td>
                  
                  {/* Company Name with Better Typography */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="text-xs font-bold text-gray-900 group-hover:text-blue-800 transition-colors duration-200">
                      {company.name}
                    </div>
                    <div className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors duration-200">
                      ID: {company.id || company._id.slice(-6)}
                    </div>
                  </td>
                  
                  {/* Email with Better Styling */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="text-xs text-gray-700 group-hover:text-gray-900 transition-colors duration-200 flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      <span className="truncate max-w-xs">{company.email}</span>
                    </div>
                  </td>
                  
                  {/* Firm with Enhanced Badge */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    {company.firm ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200 group-hover:from-blue-200 group-hover:to-indigo-200 transition-all duration-300 shadow-sm">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {company.firm.firmName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        No firm
                      </span>
                    )}
                  </td>
                  
                  {/* Enhanced Action Buttons */}
                  <td className="px-4 py-2 whitespace-nowrap text-xs font-medium">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEditClick(company)}
                        className="group/btn inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-green-500 transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <svg className="w-3 h-3 mr-1 group-hover/btn:rotate-12 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewClick(company)}
                        className="group/btn inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <svg className="w-3 h-3 mr-1 group-hover/btn:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Table Footer with Company Count */}
        {filteredCompanies.length > 0 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>
                    {searchTerm ? (
                      <>Showing <span className="font-semibold text-gray-800">{filteredCompanies.length}</span> of <span className="font-semibold text-gray-800">{companies.length}</span> companies</>
                    ) : (
                      <>Total Companies: <span className="font-semibold text-gray-800">{companies.length}</span></>
                    )}
                  </span>
                </div>
                {searchTerm && (
                  <div className="flex items-center space-x-1 text-blue-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span className="text-xs font-medium">Filtered</span>
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {!isLoading && companies.length === 0 && (
        <div className="text-center py-12 animate-fadeIn">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
          <p className="text-gray-500 mb-6">Get started by creating your first company.</p>
          <button
            onClick={() => {
              setEditCompany(null);
              setActiveTab('form');
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Create First Company
          </button>
        </div>
      )}

      {/* No Search Results State */}
      {!isLoading && companies.length > 0 && filteredCompanies.length === 0 && searchTerm && (
        <div className="text-center py-12 animate-fadeIn">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies match your search</h3>
          <p className="text-gray-500 mb-6">Try searching with different keywords or clear the search filter.</p>
          <button
            onClick={() => handleSearch('')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Clear Search
          </button>
        </div>
      )}
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        
        .animate-slideDown {
          animation: slideDown 0.8s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s ease-out;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out both;
        }
      `}</style>

      {false && editCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-60 items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl p-8 relative max-h-[90vh] overflow-y-auto transform animate-slideUp border border-gray-200">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Edit Company</h3>
                  <p className="text-gray-600 text-xs">Update company information and settings</p>
                </div>
              </div>
              <button
                onClick={handleCloseEditDialog}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 flex items-center justify-center transition-colors duration-200 group"
              >
                <svg className="w-4 h-4 text-gray-600 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form
              onSubmit={handleEditSubmit}
              ref={editFormRef}
              className="space-y-6"
            >
              {/* Error Summary */}
              {Object.keys(formErrors).length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl animate-fadeIn">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-red-800 font-semibold">Please fix the following errors:</h4>
                  </div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {Object.entries(formErrors).map(([field, error]) => (
                      <li key={field} className="flex items-center">
                        <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Form Progress Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span className="font-medium">Form Progress</span>
                  <span className="flex items-center">
                    {formProgress === 100 ? (
                      <span className="text-green-600 font-semibold flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Complete
                      </span>
                    ) : (
                      <span>{formProgress}% Complete</span>
                    )}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <div 
                    className={`h-3 rounded-full transition-all duration-500 ${
                      formProgress === 100 
                        ? 'bg-gradient-to-r from-green-400 to-green-600' 
                        : formProgress >= 75 
                        ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                        : formProgress >= 50
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-600'
                        : 'bg-gradient-to-r from-red-400 to-red-600'
                    }`}
                    style={{width: `${formProgress}%`}}
                  ></div>
                </div>
                {formProgress < 100 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Please complete all required fields to enable submission
                  </p>
                )}
              </div>

              {/* Company Information Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  Company Information
                  <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Company Name *
                    </label>
                    <input
                      name="name"
                      defaultValue={editCompany.name}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
                        formErrors.name 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      required
                      placeholder="Enter company name"
                      onChange={calculateFormProgress}
                    />
                    {formErrors.name && (
                      <p className="text-red-600 text-sm flex items-center mt-1">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        {formErrors.name}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      Email *
                    </label>
                    <input
                      name="email"
                      type="email"
                      defaultValue={editCompany.email}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
                        formErrors.email 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      required
                      placeholder="Enter email address"
                      onChange={calculateFormProgress}
                    />
                    {formErrors.email && (
                      <p className="text-red-600 text-sm flex items-center mt-1">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">Print Name *</label>
                    <input
                      name="printName"
                      defaultValue={editCompany.printName}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter print name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">Address *</label>
                    <input
                      name="address"
                      defaultValue={editCompany.address}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter company address"
                    />
                  </div>
                </div>
              </div>
              {/* Contact & Business Details Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  Contact & Business Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Phone *
                    </label>
                    <input
                      name="phone"
                      defaultValue={editCompany.phone}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">GST Number</label>
                    <input
                      name="gstNo"
                      defaultValue={editCompany.gstNo || ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                      placeholder="Enter GST number (optional)"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        name="isStockStatus"
                        defaultChecked={!!editCompany.isStockStatus}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="font-semibold text-gray-700 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        Enable Stock Management
                      </span>
                    </label>
                    <p className="text-sm text-gray-600 ml-8">Enable this option to track inventory and stock levels</p>
                  </div>
                </div>
              </div>

              {/* Banking Information Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  Banking Information
                  <span className="ml-auto text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">Bank Name *</label>
                    <input
                      name="bankName"
                      defaultValue={editCompany.bankName}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">Branch Name *</label>
                    <input
                      name="branchName"
                      defaultValue={editCompany.branchName}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter branch name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">Account Number *</label>
                    <input
                      name="accountNo"
                      defaultValue={editCompany.accountNo}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter account number"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">IFSC Code *</label>
                    <input
                      name="ifsc"
                      defaultValue={editCompany.ifsc}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter IFSC code"
                    />
                  </div>
                </div>
              </div>

              {/* File Uploads Section */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-md flex items-center justify-center mr-2">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  File Uploads
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700">Company Logo</label>
                    {/* Current Logo Preview */}
                    <div className="mb-2">
                      <div className="text-xs text-gray-600 mb-1">Current Logo:</div>
                      <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/companies/image/${editCompany._id}`}
                          alt="Current logo"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden flex-col items-center justify-center text-gray-400">
                          <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-xs">No Logo</span>
                        </div>
                      </div>
                    </div>
                    <input 
                      type="file" 
                      name="logo" 
                      accept="image/*"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" 
                    />
                    <p className="text-xs text-gray-500">Upload new logo (JPG, PNG)</p>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700">Bank QR Code</label>
                    {/* Current QR Code Preview */}
                    <div className="mb-2">
                      <div className="text-xs text-gray-600 mb-1">Current QR Code:</div>
                      <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/companies/bankQR/${editCompany._id}`}
                          alt="Current QR code"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden flex-col items-center justify-center text-gray-400">
                          <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                          <span className="text-xs">No QR</span>
                        </div>
                      </div>
                    </div>
                    <input 
                      type="file" 
                      name="bankQR" 
                      accept="image/*"
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" 
                    />
                    <p className="text-xs text-gray-500">Upload new QR code (JPG, PNG)</p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700">QR Code Name</label>
                    <input
                      name="qrName"
                      defaultValue={editCompany.qrName || ''}
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                      placeholder="Enter QR code name (e.g., Payment QR, UPI QR)"
                    />
                  </div>
                </div>
              </div>

              {/* Tally Integration Section */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  Tally Integration
                  <span className="ml-auto text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">Required</span>
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block font-semibold text-gray-700">Tally Server URL *</label>
                    <input
                      name="tallyUrl"
                      defaultValue={editCompany.tallyUrl || ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm hover:shadow-md invalid:border-red-500 invalid:ring-red-500"
                      required
                      placeholder="Enter Tally server URL (e.g., http://localhost:9000)"
                    />
                    <p className="text-sm text-gray-600">URL of the Tally server for integration</p>
                  </div>
                </div>
              </div>
              {renderTallyControls()}

              {editCompany._id && (
                <VoucherTypeSelector
                  companyId={editCompany._id}
                  selectedVoucherTypes={selectedVoucherTypes}
                  onChange={setSelectedVoucherTypes}
                />
              )}

              <TermsAndConditionsManager
                title="Quotation Terms and Conditions"
                terms={quotationTermsAndConditions}
                setTerms={setQuotationTermsAndConditions}
                placeholder="Enter quotation term or condition"
              />

              <TermsAndConditionsManager
                title="Delivery Order Terms and Conditions"
                terms={deliveryOrderTermsAndConditions}
                setTerms={setDeliveryOrderTermsAndConditions}
                placeholder="Enter delivery order term or condition"
              />

              <DomainAccessManager
                domains={domains}
                setDomains={setDomains}
                defaultAccess={{
                  admin: defaultAdminAccess,
                  companyHead: defaultCompanyHeadAccess,
                  employee: defaultEmployeeAccess,
                }}
              />

              <input type="hidden" name="ADMINMAILID" value={editCompany.ADMINMAILID || ''} />
              <input type="hidden" name="APPLICATIONPATH" value={editCompany.APPLICATIONPATH || ''} />
              <input type="hidden" name="DATAPATH" value={editCompany.DATAPATH || ''} />
              <input type="hidden" name="ISLICCLIENTMODE" value={editCompany.ISLICCLIENTMODE ? 'on' : 'off'} />
              <input type="hidden" name="ISREMOTEACCESSMODE" value={editCompany.ISREMOTEACCESSMODE ? 'on' : 'off'} />
              <input type="hidden" name="SERIALNUMBER" value={editCompany.SERIALNUMBER || ''} />

              <div className="col-span-4 flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                {/* Form Status Info */}
                <div className="flex items-center space-x-4">
                  {formProgress < 100 && (
                    <div className="flex items-center text-amber-600 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span>Complete all required fields to enable save</span>
                    </div>
                  )}
                  {Object.keys(formErrors).length > 0 && (
                    <div className="flex items-center text-red-600 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{Object.keys(formErrors).length} error{Object.keys(formErrors).length !== 1 ? 's' : ''} to fix</span>
                    </div>
                  )}
                  {formProgress === 100 && Object.keys(formErrors).length === 0 && !loadingSubmit && (
                    <div className="flex items-center text-green-600 text-sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Form is ready to submit</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleCloseEditDialog}
                    className="group px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 flex items-center space-x-2"
                    disabled={loadingSubmit}
                  >
                    <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    className={`group px-6 py-3 border border-transparent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2 shadow-lg ${
                      loadingSubmit
                        ? 'bg-gray-400 cursor-not-allowed'
                        : formProgress === 100 && Object.keys(formErrors).length === 0
                        ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:ring-green-500 transform hover:scale-105'
                        : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                    disabled={loadingSubmit || formProgress < 100 || Object.keys(formErrors).length > 0}
                  >
                    {loadingSubmit ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving Changes...</span>
                      </>
                    ) : formProgress === 100 && Object.keys(formErrors).length === 0 ? (
                      <>
                        <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Save Changes</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Complete Form to Save</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {false && (
        <div className="fixed z-50 inset-0 overflow-y-auto flex items-center justify-center bg-black bg-opacity-75 animate-fadeIn backdrop-blur-md">
          <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl p-8 relative max-h-[90vh] overflow-y-auto transform animate-slideUp border border-gray-200">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Create Company</h3>
                  <p className="text-gray-600 text-sm">Set up a new company with all necessary details</p>
                </div>
              </div>
              <button
                onClick={handleCloseCreateDialog}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 flex items-center justify-center transition-colors duration-200 group"
              >
                <svg className="w-4 h-4 text-gray-600 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form
              onSubmit={handleCreateSubmit}
              ref={createFormRef}
              className="space-y-4 grid grid-cols-4 gap-x-4 gap-y-2"
            >
              <div>
                <label className="block font-semibold mb-1">Company Name *</label>
                <input name="name" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Email *</label>
                <input
                  name="email"
                  type="email"
                  className="border p-2 w-full"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Print Name *</label>
                <input name="printName" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Address *</label>
                <input name="address" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Phone *</label>
                <input name="phone" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">GST No</label>
                <input name="gstNo" className="border p-2 w-full" />
              </div>
              <div>
                <label className="block font-semibold mb-1">Stock Status</label>
                <input type="checkbox" name="isStockStatus" />
              </div>
              <div className="md:col-span-2">
                <label className="block font-semibold mb-1">Dealer References</label>
                <Select
                  isMulti
                  options={allDealers}
                  value={allDealers.filter(d => selectedDealerRefs.includes(d.value))}
                  onChange={(selected) => setSelectedDealerRefs(selected ? selected.map(s => s.value) : [])}
                  placeholder="Search and select dealers..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isClearable
                  onMenuOpen={() => { if (allDealers.length === 0) fetchDealers(); }}
                  onInputChange={(val) => { if (val.length >= 2) fetchDealers(val); }}
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Bank Name *</label>
                <input name="bankName" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Branch Name *</label>
                <input name="branchName" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Account No *</label>
                <input name="accountNo" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">IFSC *</label>
                <input name="ifsc" className="border p-2 w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Upload Logo *</label>
                <input type="file" name="logo" className="w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">Upload Bank QR *</label>
                <input type="file" name="bankQR" className="w-full" required />
              </div>
              <div>
                <label className="block font-semibold mb-1">QR Name</label>
                <input
                  name="qrName"
                  className="border p-2 w-full"
                  placeholder="Enter QR code name (e.g., Payment QR, UPI QR)"
                />
              </div>
              
              <TermsAndConditionsManager
                title="Quotation Terms and Conditions"
                terms={quotationTermsAndConditions}
                setTerms={setQuotationTermsAndConditions}
                placeholder="Enter quotation term or condition"
              />

              <TermsAndConditionsManager
                title="Delivery Order Terms and Conditions"
                terms={deliveryOrderTermsAndConditions}
                setTerms={setDeliveryOrderTermsAndConditions}
                placeholder="Enter delivery order term or condition"
              />
              
              <DomainAccessManager
                domains={domains}
                setDomains={setDomains}
                defaultAccess={{
                  admin: defaultAdminAccess,
                  companyHead: defaultCompanyHeadAccess,
                  employee: defaultEmployeeAccess,
                }}
              />
              <div className="flex justify-end space-x-4 col-span-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseCreateDialog}
                  className="group px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  className="group px-6 py-3 border border-transparent rounded-lg text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 flex items-center space-x-2 shadow-lg"
                  disabled={loadingSubmit}
                >
                  {loadingSubmit ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Create Company</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

        </div>
      )}

      <ViewCompanyModal 
        company={viewCompany}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
      
      {/* Enhanced CSS Styles */}
      <style>{`
        /* Form input focus effects */
        .form-input:focus {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
        
        /* Button hover effects */
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(59, 130, 246, 0.3);
        }
        
        .btn-secondary:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
        
        /* Table row hover effects */
        .table-row:hover {
          transform: translateX(5px);
          box-shadow: 5px 0 15px rgba(0, 0, 0, 0.1);
        }
        
        /* Loading spinner enhancement */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .loading-spinner {
          animation: spin 1s linear infinite;
        }
        
        /* Modal backdrop blur */
        .modal-backdrop {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        
        /* Smooth transitions for all interactive elements */
        * {
          transition: all 0.2s ease-in-out;
        }
        
        /* Custom scrollbar for modals */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        
        /* Gradient text effects */
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        /* Floating labels effect */
        .floating-label {
          transform: translateY(-25px) scale(0.875);
          color: #3b82f6;
        }
        
        /* Card hover effects */
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
        }
        
        /* Progress bar animation */
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        
        .progress-bar {
          animation: progress 2s ease-in-out;
        }
        
        /* Pulse effect for important elements */
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .pulse-effect {
          animation: pulse 2s infinite;
        }
        
        /* Slide in effects */
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .slide-in-left {
          animation: slideInLeft 0.6s ease-out;
        }
        
        .slide-in-right {
          animation: slideInRight 0.6s ease-out;
        }
        
        /* Enhanced focus rings */
        .focus-ring:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
        }
        
        /* Ripple effect on click */
        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }
        
        .ripple-effect {
          position: relative;
          overflow: hidden;
        }
        
        .ripple-effect::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }
        
        .ripple-effect:active::before {
          width: 300px;
          height: 300px;
        }
      `}</style>
    </div>
  );
};

export default CompanyManagement;